/**
 * Copyright (c) 2026 Diversabilities / Queensland Compassionate Services.
 * Licensed under the ISC License.
 *
 * Smart Recommendation Hub — Widget
 *
 * Fetches and renders AI-powered resource recommendations per category tab with pagination.
 */
import $ from 'jquery';

(function () {
  function escapeHtml(str) {
    if (!str) {
      return '';
    }
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  const SELECTOR_CATEGORY_TAB = '.category-tab';
  const SELECTOR_TAB_PANE = '.tab-pane';
  const SELECTOR_LOADING = '.ai-recommendations-loading';
  const CLASS_ACTIVE = 'active';
  const CLASS_SHOW_ACTIVE = 'show active';
  const ATTR_ARIA_SELECTED = 'aria-selected';
  const DEFAULT_PER_PAGE = 6;
  const SCROLL_ANIMATION_MS = 300;
  const SCROLL_OFFSET_PX = 100;
  const AJAX_TIMEOUT_MS = 10000;

  function getI18n() {
    return Object.assign({
      play_video: 'Play Video',
      listen_podcast: 'Listen Podcast',
      view_resource: 'View Resource',
      view_details: 'View Details',
      category_fallback: 'Resource',
    }, window.dvSmartRecHub?.i18n || {});
  }

  function buildCategoriesHtml(item) {
    const fallback = getI18n().category_fallback;
    if (Array.isArray(item.categories) && item.categories.length > 0) {
      return item.categories.map(cat => {
        const catName = escapeHtml(cat.name || fallback);
        const catLink = cat.link || '#';
        const i18nAttr = cat.term_id ? ` data-i18n="term_${cat.term_id}"` : '';
        return `<a href="${catLink}"><span${i18nAttr}>${catName}</span></a>`;
      }).join('');
    }
    const name = escapeHtml(item.category_name || fallback);
    const link = item.category_link || '#';
    return `<a href="${link}"><span>${name}</span></a>`;
  }

  function getItemData(item) {
    return {
      title: escapeHtml(item.title),
      excerpt: escapeHtml(item.excerpt),
      url: item.wp_permalink || '#',
      categoryName: escapeHtml(item.category_name || getI18n().category_fallback),
      categoryLink: item.category_link || '#',
      categoriesHtml: buildCategoriesHtml(item),
      thumb: item.thumbnail || '',
      authorName: escapeHtml(item.author_name || 'Diversabilities'),
      authorAvatar: item.author_avatar || '',
      publishDate: escapeHtml(item.publish_date || ''),
      postId: item.post_id || item.id,
    };
  }

  function renderVideoCard(d) {
    return `
      <div class="col-md-6 col-xl-4">
        <article id="post-${d.postId}" data-post-id="${d.postId}" class="blog-card videos-card">
          <div class="blog-card-thumb video-thumb">
            <a href="${d.url}">
              <img src="${d.thumb}" alt="${d.title}" width="386" height="250">
            </a>
          </div>
          <div class="blog-card-content">
            <div class="content-category">
              <div class="content-category-tag">
                ${d.categoriesHtml}
              </div>
            </div>
            <div class="content-title">
              <h2 class="h5 entry-title"><a href="${d.url}" data-translatable data-field="title">${d.title}</a></h2>
            </div>
            <div class="content-description">
              <p data-translatable data-field="excerpt">${d.excerpt}</p>
            </div>
            <div class="entry-meta content-author">
              <div class="content-btn">
                <a href="${d.url}" class="btn btn-outline-primary">
                  <span data-i18n="card_play_video">${escapeHtml(getI18n().play_video)}</span>
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    `;
  }

  function renderPodcastCard(d) {
    return `
      <div class="col-md-6 col-xl-4">
        <article id="post-${d.postId}" data-post-id="${d.postId}" class="blog-card podcasts-card">
          <div class="blog-card-thumb">
            <a href="${d.url}">
              <img width="386" height="250" src="${d.thumb}" alt="${d.title}">
            </a>
          </div>
          <div class="blog-card-content">
            <div class="content-category">
              <div class="content-category-tag">
                ${d.categoriesHtml}
              </div>
            </div>
            <div class="content-title">
              <h2 class="h5 entry-title"><a href="${d.url}" data-translatable data-field="title">${d.title}</a></h2>
            </div>
            <div class="content-description">
              <p data-translatable data-field="excerpt">${d.excerpt}</p>
            </div>
            <div class="entry-meta content-author">
              <div class="content-btn">
                <a href="${d.url}" class="btn btn-outline-primary">
                  <span data-i18n="card_listen_podcast">${escapeHtml(getI18n().listen_podcast)}</span>
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    `;
  }

  function renderLibraryCard(d) {
    return `
      <div class="col-md-6 col-xl-4">
        <article id="post-${d.postId}" data-post-id="${d.postId}" class="blog-card library-card">
          <div class="blog-card-thumb">
            <a href="${d.url}">
              <img width="386" height="250" src="${d.thumb}" alt="${d.title}">
            </a>
          </div>
          <div class="blog-card-content">
            <div class="content-category">
              <div class="content-category-tag">
                ${d.categoriesHtml}
              </div>
            </div>
            <div class="content-title">
              <h2 class="h5 entry-title"><a href="${d.url}" data-translatable data-field="title">${d.title}</a></h2>
            </div>
            <div class="entry-meta content-author">
              <div class="content-btn">
                <a href="${d.url}" class="btn btn-outline-primary">
                  <span data-i18n="card_view_resource">${escapeHtml(getI18n().view_resource)}</span>
                </a>
              </div>
            </div>
          </div>
        </article>
      </div>
    `;
  }

  function renderLinkCard(d) {
    return `
      <div class="col-md-6 col-xl-4">
        <div class="service-item-wrapper w-100" data-post-id="${d.postId}">
          <div class="service-item">
            <div class="service-item__logo">
              <img src="${d.thumb}" alt="${d.title}">
            </div>
            <div class="service-item__content-wrapper">
              <div class="d-flex gap-3 w-100 justify-content-between">
                <h5 class="service-item-title" data-translatable data-field="title">${d.title}</h5>
              </div>
              <div class="service-item-category-wrapper">
                <div class="service-item-category">
                  <p>${d.categoryName}</p>
                </div>
              </div>
              <div class="service-item-description">
                <p data-translatable data-field="excerpt">${d.excerpt}</p>
              </div>
              <div class="service-item-button-wrapper">
                <a href="${d.url}" class="view-details btn btn-outline-primary">
                  <span data-i18n="card_view_details">${escapeHtml(getI18n().view_details)}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function renderGalleryCard(d) {
    return `
      <div class="col-md-6 col-xl-3">
        <div class="gallery-card" data-post-id="${d.postId}">
          <a href="${d.url}" class="gallery-card-link">
            <img alt="${d.title}" width="327" height="327" src="${d.thumb}" class="gallery-card-img">
          </a>
          <h2 class="h5 mt-2" data-translatable data-field="title">${d.title}</h2>
        </div>
      </div>
    `;
  }

  function renderBlogCard(d) {
    let postedOnMarkup = '';
    if (d.publishDate) {
      postedOnMarkup = `<span class="posted-on"><time>${d.publishDate}</time></span>`;
    }

    return `
      <div class="col-md-6 col-xl-4">
        <article id="post-${d.postId}" data-post-id="${d.postId}" class="blog-card">
          <div class="blog-card-thumb">
            <a href="${d.url}">
              <img width="386" height="250" src="${d.thumb}" alt="${d.title}">
            </a>
          </div>
          <div class="blog-card-content">
            <div class="content-category">
              <div class="content-category-tag">
                ${d.categoriesHtml}
              </div>
            </div>
            <div class="content-title">
              <h2 class="h5 entry-title"><a href="${d.url}" data-translatable data-field="title">${d.title}</a></h2>
            </div>
            <div class="content-description">
              <p data-translatable data-field="excerpt">${d.excerpt}</p>
            </div>
            <div class="entry-meta content-author">
              <span class="byline d-flex">
                <span class="author-profile">
                  <img src="${d.authorAvatar}" width="48" height="48" alt="${d.authorName}">
                </span>
                <span class="meta-details d-flex flex-column">
                  <span class="author vcard">${d.authorName}</span>
                  ${postedOnMarkup}
                </span>
              </span>
            </div>
          </div>
        </article>
      </div>
    `;
  }

  const cardRenderers = {
    videos: renderVideoCard,
    podcasts: renderPodcastCard,
    library: renderLibraryCard,
    links: renderLinkCard,
    gallery: renderGalleryCard,
    blogs: renderBlogCard,
  };

  function renderCardHTML(item) {
    const data = getItemData(item);
    const renderer = cardRenderers[item.normalized_post_type] || cardRenderers.blogs;
    return renderer(data);
  }

  function initSmartRecommendationHub() {
    const $hub = $('#smart-recommendation-hub');
    if (!$hub.length) {
      return;
    }

    const postId = $hub.data('post-id');
    if (!postId) {
      return;
    }

    let defaultTab = $hub.data('default-tab') || 'blogs';
    if (['events', 'services'].includes(defaultTab)) {
      defaultTab = 'blogs';
    }
    const perPage = parseInt($hub.data('per-page'), 10) || DEFAULT_PER_PAGE;
    const recommendationsCache = {};

    // Resolve REST API URL handling site subdirectories (e.g. /diversability/)
    let restUrl = $hub.data('rest-url');
    if (!restUrl) {
      let subfolder = '';
      const parts = window.location.pathname.split('/').filter(Boolean);
      if (parts.length > 0 && !['resources', 'wp-admin', 'wp-json', 'events', 'service'].includes(parts[0])) {
        subfolder = `/${parts[0]}`;
      }
      restUrl = `${window.location.origin}${subfolder}/wp-json/diversabilities/v1/recommendations`;
    }

    function buildFetchUrl(cat, page) {
      let paramPrefix = '?id=';
      if (restUrl.indexOf('?') !== -1) {
        paramPrefix = '&id=';
      }
      return `${restUrl}${paramPrefix}${postId}&type=${cat}&page=${page}&per_page=${perPage}`;
    }

    function buildNavButton(direction, page, isDisabled) {
      let disabledClass = '';
      let disabledAttr = '';
      if (isDisabled) {
        disabledClass = ' disabled';
        disabledAttr = ' disabled="disabled"';
      }

      let label = 'Next page';
      if (direction === 'prev') {
        label = 'Previous page';
      }

      return `<button type="button" class="page-numbers ${direction}${disabledClass}" data-page="${page}"${disabledAttr}>
        <span class="${direction}-icon"></span>
        <span class="visually-hidden">${label}</span>
      </button>`;
    }

    function isPageVisible(page, currentPage, totalPages) {
      return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2;
    }

    function buildPageNumbersHtml(currentPage, totalPages) {
      let html = '';
      let showDots = false;
      for (let i = 1; i <= totalPages; i++) {
        if (isPageVisible(i, currentPage, totalPages)) {
          let currentClass = '';
          if (i === currentPage) {
            currentClass = ' current active';
          }
          html += `<button type="button" class="page-numbers${currentClass}" data-page="${i}">${i}</button>`;
          showDots = false;
        } else if (!showDots) {
          html += `<span class="page-numbers dots">...</span>`;
          showDots = true;
        } else {
          // No action required when dots are already rendered for hidden pages
        }
      }
      return html;
    }

    function renderPagination(cat, data) {
      const $pagination = $(`[data-category-pagination="${cat}"]`, $hub);
      if (!$pagination.length) {
        return;
      }

      const totalPages = parseInt(data.total_pages || 1, 10);
      const currentPage = parseInt(data.current_page || 1, 10);

      if (totalPages <= 1) {
        $pagination.empty();
        return;
      }

      let html = '<div class="pagination pagination-two text-center my-4">';
      html += buildNavButton('prev', currentPage - 1, currentPage <= 1);
      html += buildPageNumbersHtml(currentPage, totalPages);
      html += buildNavButton('next', currentPage + 1, currentPage >= totalPages);
      html += '</div>';

      $pagination.html(html);
    }

    function renderCategoryContent(cat, data) {
      const $grid = $(`[data-category-grid="${cat}"]`, $hub);
      if (!$grid.length) {
        return;
      }

      $grid.empty();
      let results = [];
      if (data && Array.isArray(data.results)) {
        results = data.results;
      }

      if (results.length > 0) {
        results.forEach(item => {
          $grid.append(renderCardHTML(item));
        });
        renderPagination(cat, data);
      } else {
        $grid.append('<div class="col-12"><p class="no-post text-center py-4">No recommendations available in this category.</p></div>');
        $(`[data-category-pagination="${cat}"]`, $hub).empty();
      }

      // Cards are inserted after the page's initial language-switcher pass,
      // so re-run its translation pipeline against the freshly-added
      // [data-post-id]/[data-translatable]/[data-i18n] elements — otherwise
      // they'd stay in English until the next full language switch.
      document.dispatchEvent(new CustomEvent('dv:content-updated'));
    }

    function fetchCategoryRecommendations(cat, page = 1) {
      const cacheKey = `${cat}_page_${page}`;
      if (recommendationsCache[cacheKey]) {
        renderCategoryContent(cat, recommendationsCache[cacheKey]);
        $(SELECTOR_LOADING, $hub).addClass('d-none').hide();
        $hub.removeClass('d-none').show();
        return;
      }

      $(SELECTOR_LOADING, $hub).removeClass('d-none').show();

      $.ajax({
        url: buildFetchUrl(cat, page),
        method: 'GET',
        dataType: 'json',
        timeout: AJAX_TIMEOUT_MS,
        success(res) {
          if (res?.status && res?.data) {
            recommendationsCache[cacheKey] = res.data;
            renderCategoryContent(cat, res.data);
          } else {
            renderCategoryContent(cat, { results: [], total_pages: 1, current_page: 1 });
          }
          $(SELECTOR_LOADING, $hub).addClass('d-none').hide();
          $hub.removeClass('d-none').show();
        },
        error() {
          renderCategoryContent(cat, { results: [], total_pages: 1, current_page: 1 });
          $(SELECTOR_LOADING, $hub).addClass('d-none').hide();
          $hub.removeClass('d-none').show();
        }
      });
    }

    // Tab Click Listener: Fetch recommendations for clicked category tab
    $hub.off('click.categoryTab').on('click.categoryTab', SELECTOR_CATEGORY_TAB, function (e) {
      e.preventDefault();
      const cat = $(this).data('category');
      if (!cat) {
        return;
      }

      $(SELECTOR_CATEGORY_TAB, $hub).removeClass(CLASS_ACTIVE).attr(ATTR_ARIA_SELECTED, 'false');
      $(SELECTOR_TAB_PANE, $hub).removeClass(CLASS_SHOW_ACTIVE);

      $(this).addClass(CLASS_ACTIVE).attr(ATTR_ARIA_SELECTED, 'true');
      $(`#${cat}`, $hub).addClass(CLASS_SHOW_ACTIVE);

      fetchCategoryRecommendations(cat, 1);
    });

    // Pagination Click Listener
    $hub.off('click.hubPagination').on('click.hubPagination', '[data-category-pagination] .page-numbers', function (e) {
      e.preventDefault();
      const $btn = $(this);
      if ($btn.hasClass('disabled') || $btn.hasClass('current') || $btn.is(':disabled') || $btn.hasClass('dots')) {
        return;
      }

      const page = parseInt($btn.data('page'), 10);
      const cat = $btn.closest('[data-category-pagination]').data('category-pagination');

      if (cat && page && !isNaN(page)) {
        fetchCategoryRecommendations(cat, page);
        let hubOffset = 0;
        if ($hub.offset()) {
          hubOffset = $hub.offset().top - SCROLL_OFFSET_PX;
        }
        if (hubOffset > 0) {
          $('html, body').animate({ scrollTop: hubOffset }, SCROLL_ANIMATION_MS);
        }
      }
    });

    // Initial Load: Fetch recommendations for the default active tab
    fetchCategoryRecommendations(defaultTab, 1);
  }

  $(document).ready(function () {
    initSmartRecommendationHub();
  });
})();
